// src/components/Pagination/Pagination.tsx
import React, { useState } from "react";
import { Flex, Button, Select, Text, Box, Skeleton } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";

interface PaginationProps {
  totalItems: number;
  onPageChange: (offset: number, limit: number) => void;
  useUrlParams?: boolean;
  defaultPage?: number;
  defaultLimit?: number;
  isLoading?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  totalItems,
  onPageChange,
  useUrlParams = true,
  defaultPage = 1,
  defaultLimit = 20,
  isLoading = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localPage, setLocalPage] = useState(defaultPage);
  const [localLimit, setLocalLimit] = useState(defaultLimit);

  const currentPage = useUrlParams ? Number(searchParams.get("page")) || 1 : localPage;
  const itemsPerPage = useUrlParams ? Number(searchParams.get("limit")) || 20 : localLimit;

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    if (useUrlParams) {
      setSearchParams({ page: String(page), limit: String(itemsPerPage) });
    } else {
      setLocalPage(page);
    }
    const offset = (page - 1) * itemsPerPage;
    onPageChange(offset, itemsPerPage);
  };

  const handleItemsPerPageChange = (value: number) => {
    if (useUrlParams) {
      setSearchParams({ page: "1", limit: String(value) });
    } else {
      setLocalPage(1);
      setLocalLimit(value);
    }
    onPageChange(0, value);
  };

  if (isLoading) {
    return (
      <Box p={4} borderRadius="md" boxShadow="md">
        <Flex justifyContent="space-between" alignItems="center">
          <Skeleton height="20px" width="200px" borderRadius="4px" />
          <Flex gap="8px">
            <Skeleton height="32px" width="32px" borderRadius="4px" />
            <Skeleton height="32px" width="32px" borderRadius="4px" />
            <Skeleton height="32px" width="32px" borderRadius="4px" />
            <Skeleton height="32px" width="32px" borderRadius="4px" />
          </Flex>
        </Flex>
      </Box>
    );
  }

  return (
    <Box p={4} borderRadius="md" boxShadow="md">
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        <Flex alignItems="center">
          <Text mr={2} fontWeight="bold">
            Показать:
          </Text>
          <Select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            width="auto"
            bg="gray.100"
            borderColor="gray.300"
            _hover={{ borderColor: "#2563eb" }}
            focusBorderColor="#2563eb"
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Select>
          <Text ml={2}>элементов</Text>
        </Flex>
        <Flex>
          <Button
            onClick={() => handlePageChange(1)}
            isDisabled={currentPage === 1}
            mr={2}
            bg="#2563eb"
            color="white"
            _hover={{ bg: "#2563eb" }}
            _active={{ bg: "#2563eb" }}
          >
            {"<<"}
          </Button>
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            isDisabled={currentPage === 1}
            mr={2}
            bg="#2563eb"
            color="white"
            _hover={{ bg: "#2563eb" }}
            _active={{ bg: "#2563eb" }}
          >
            {"<"}
          </Button>
          <Text mx={2} fontWeight="bold" display="flex" alignItems="center">
            Страница {currentPage} из {totalPages}
          </Text>
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            isDisabled={currentPage === totalPages}
            ml={2}
            bg="#2563eb"
            color="white"
            _hover={{ bg: "#2563eb" }}
            _active={{ bg: "#2563eb" }}
          >
            {">"}
          </Button>
          <Button
            onClick={() => handlePageChange(totalPages)}
            isDisabled={currentPage === totalPages}
            ml={2}
            bg="#2563eb"
            color="white"
            _hover={{ bg: "#2563eb" }}
            _active={{ bg: "#2563eb" }}
          >
            {">>"}
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};
